import { useCallback, useMemo } from 'react'
import { Power, PowerOff, Zap } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchRules, toggleRule, FRAUD_STR as S, type FraudRuleRow } from './fraudApi'

/**
 * A25.08 — Автоматические меры. The action each detection rule takes
 * automatically (flag/block/review) and whether the automation is armed. Wires
 * the REAL `/admin/antifraud/rules` feed + toggle. Placeholder permission USERS_VIEW.
 */
export function AutoActionsScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<FraudRuleRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      {
        key: 'action',
        header: S.action,
        type: 'enum',
        options: [
          { value: 'flag', label: S.actionFlag, status: 'review' },
          { value: 'block', label: S.actionBlock, status: 'blocked' },
          { value: 'review', label: S.actionReview, status: 'pending' },
        ],
      },
      {
        key: 'enabled',
        header: S.enabled,
        type: 'enum',
        options: [
          { value: 'true', label: S.enabled, status: 'active' },
          { value: 'false', label: S.disable, status: 'suspended' },
        ],
        accessor: (r) => String(r.enabled),
      },
      { key: 'hits', header: S.hits, type: 'number' },
    ],
    [],
  )

  const toggle = useCallback(
    async (row: FraudRuleRow) => {
      try {
        await toggleRule(row.id, !row.enabled)
        toast(S.toggled, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<FraudRuleRow>[]>(
    () => [
      {
        id: 'disable',
        label: S.disable,
        icon: <PowerOff size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: toggle,
      },
      {
        id: 'enable',
        label: S.enable,
        icon: <Power size={14} />,
        visible: (r) => !r.enabled,
        onSelect: toggle,
      },
    ],
    [toggle],
  )

  const fetcher = useCallback(fetchRules, [])
  return (
    <>
      <PageHeader title={S.autoActions} screenId="A25.08" />
      <DataTable<FraudRuleRow>
        screenId="A25.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Zap size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
